import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import { MapPin, Navigation, Maximize2, Minimize2, ExternalLink, Train, GraduationCap, Cross, ShoppingBag, Lock, Crown, ArrowRight, Sparkles } from "lucide-react";

/**
 * v2.39 — PropertyMiniMap
 * Leaflet+OSM map for the property detail page (replaces the old Google
 * Maps iframe). Shows the property pin, supports fullscreen, and exposes
 * 1-tap "Get Directions" + "Open in Google Maps".
 *
 * Falls back to a soft "location not pinned" panel if no coordinates exist.
 */

const propertyIcon = L.divIcon({
  className: "house-entry-detail-pin",
  html: `<div style="filter: drop-shadow(0 6px 12px rgba(0,85,255,0.5));">
    <svg viewBox="0 0 24 32" width="34" height="44">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z"
        fill="#0055FF" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4.5" fill="white"/>
    </svg>
  </div>`,
  iconSize: [34, 44],
  iconAnchor: [17, 44],
});

const NEARBY_DEFAULTS = [
  { Icon: Train, label: "Metro / Station" },
  { Icon: GraduationCap, label: "Schools" },
  { Icon: Cross, label: "Hospitals" },
  { Icon: ShoppingBag, label: "Markets" },
];

export default function PropertyMiniMap({ property, approximate = false }) {
  const [fs, setFs] = useState(false);
  const exactLat = Number(property?.latitude);
  const exactLng = Number(property?.longitude);
  const hasExactCoords =
    !Number.isNaN(exactLat) && !Number.isNaN(exactLng) && exactLat !== 0 && exactLng !== 0;

  // v2.85 Phase A — Privacy-safe approximate coords. The backend rounds
  // stored lat/lng to 3 decimals and emits them as `approx_latitude` /
  // `approx_longitude` for anon / non-plan / marketplace viewers. Exact
  // `latitude` / `longitude` come back null in that mode. We render the
  // 300m circle on top of `approx_*` whenever exact coords are absent.
  const approxLat = Number(property?.approx_latitude);
  const approxLng = Number(property?.approx_longitude);
  const hasApproxCoords =
    !Number.isNaN(approxLat) && !Number.isNaN(approxLng) && approxLat !== 0 && approxLng !== 0;

  // `map_locked` is a server-side premium / marketplace gate. When it's
  // on AND we have ANY coords (exact OR approx), we render approximate
  // mode (300m circle, no pin). The `approximate` prop (passed from
  // PropertyDetail for anon viewers) reinforces this.
  const isApproximate = approximate || Boolean(property?.map_locked) || (!hasExactCoords && hasApproxCoords);
  const lat = hasExactCoords ? exactLat : approxLat;
  const lng = hasExactCoords ? exactLng : approxLng;
  const hasCoords = hasExactCoords || hasApproxCoords;
  const approxRadius = Math.max(50, Number(property?.approx_precision_m) || 300);

  // v2.85 Phase A — Anonymous viewers always lack coords (stripped server-side)
  // and most listings carry `map_locked=true`. The earlier behaviour showed
  // a huge "Exact location & map locked" panel even when the approximate
  // area was perfectly visible — that triggered the public-location
  // regression: users felt locked out of approximate location.
  //
  // Fix: surface the approximate area / sub_area as the PRIMARY panel
  // whenever we can; decorate with a small upgrade chip when
  // `map_locked=true`. Only fall back to the legacy big-lock panel when
  // we have neither coords NOR any approximate location text.
  const approxAreaText = [property?.sub_area, property?.main_area || property?.area]
    .filter(Boolean).join(" · ");
  const hasApproxArea = !!approxAreaText;

  // v2.70 — Truly locked: no coords AND no approximate area we can show.
  if (property?.map_locked && !hasCoords && !hasApproxArea) {
    return (
      <div
        className="relative rounded-2xl overflow-hidden border-2 border-[#FDE68A] bg-gradient-to-br from-[#FFFBEB] via-white to-[#FEF3C7] p-6 sm:p-8"
        data-testid="property-mini-map-locked"
      >
        {/* Blurred fake map tile pattern as backdrop */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(45deg, #0055FF 25%, transparent 25%), linear-gradient(-45deg, #0055FF 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #0055FF 75%), linear-gradient(-45deg, transparent 75%, #0055FF 75%)",
            backgroundSize: "32px 32px",
            backgroundPosition: "0 0, 0 16px, 16px -16px, -16px 0",
            filter: "blur(2px)",
          }}
        />
        <div className="relative text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#0055FF] to-[#7C3AED] text-white shadow-lg shadow-blue-500/30 mb-4">
            <Lock size={22} />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full bg-white border border-[#FDE68A] text-[10px] uppercase tracking-[0.18em] font-bold text-[#92400E]">
            <Crown size={10} /> Premium Feature
          </div>
          <h3 className="font-heading font-bold text-xl sm:text-2xl tracking-tight text-zinc-900 mb-2">
            Exact location & map locked
          </h3>
          <p className="text-[13px] text-zinc-600 max-w-md mx-auto mb-5 leading-relaxed">
            Upgrade your plan to unlock the exact property pin, nearby map, and turn-by-turn directions.
            Free users can see <b>area</b> and <b>locality</b> only.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2.5 mb-4">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 btn-gradient rounded-xl px-5 py-2.5 text-[13px] font-bold shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-95 transition-all"
              data-testid="map-locked-upgrade-btn"
            >
              <Sparkles size={13} /> Upgrade to unlock <ArrowRight size={13} />
            </Link>
            <Link
              to="/plans-preview"
              className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-700 rounded-xl px-4 py-2.5 text-[12.5px] font-semibold hover:border-[#0055FF] hover:text-[#0055FF] hover:bg-[#EEF4FF] active:scale-95 transition-all"
              data-testid="map-locked-compare-btn"
            >
              Compare plans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const gmapsUrl = hasExactCoords
    ? `https://maps.google.com/?q=${exactLat},${exactLng}`
    : property?.google_maps_link || null;
  const directionsUrl = hasExactCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${exactLat},${exactLng}`
    : null;

  if (!hasCoords) {
    // Graceful fallback — no coords stored yet (legacy listings) OR
    // coords scrubbed for anon/non-premium viewers. v2.85 Phase A surfaces
    // sub_area / main_area so the section never feels blank. The user
    // can ALWAYS see approximate location text here even if `map_locked`
    // hides the precise pin.
    const areaText = approxAreaText;
    return (
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 text-center" data-testid="property-mini-map-fallback">
        <MapPin size={20} className="mx-auto text-zinc-400 mb-2" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1" data-testid="approx-location-label">
          Approximate Location
        </div>
        <div className="text-[14px] font-semibold text-zinc-800" data-testid="approx-location-text">
          {areaText || "Location"}
        </div>
        {/* Public/anon users keep seeing area + sub-area here. Only
            the EXACT pin (lat/lng) and directions stay behind the
            premium gate; approximate location is always public-safe. */}
        {property?.map_locked ? (
          <div className="mt-3 text-[12px] text-zinc-500 leading-relaxed" data-testid="approx-location-locked-note">
            Exact pin & turn-by-turn directions are a <b className="text-zinc-700">premium feature</b>.
            <Link to="/pricing" className="ml-1 inline-flex items-center gap-1 text-[12px] font-bold text-[#0055FF] hover:underline" data-testid="approx-location-upgrade-link">
              <Sparkles size={11} /> Upgrade
            </Link>
          </div>
        ) : (
          <div className="text-[12px] text-zinc-500 mt-1">
            Exact location coordinates not pinned for this listing yet.
          </div>
        )}
        {gmapsUrl && !property?.map_locked && (
          <a href={gmapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-[12px] font-bold text-[#0055FF] hover:underline">
            <ExternalLink size={11} /> Try area on Google Maps
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={fs ? "fixed inset-0 z-50 bg-white" : "relative"} data-testid="property-mini-map">
      {/* Header */}
      <div className={`flex items-center justify-between mb-3 ${fs ? "px-5 pt-5" : ""}`}>
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-500 inline-flex items-center gap-1.5">
          <MapPin size={11} className="text-[#0055FF]" /> {isApproximate ? "Approximate Location" : "Exact Location"}
        </div>
        <div className="flex items-center gap-1.5">
          {/* v2.85 Phase A — Hide Directions / Google Maps deep links when
              the map is rendered in `approximate` mode so the exact pin
              is never exposed even by external-link follow. */}
          {!isApproximate && directionsUrl && (
            <a
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
              className="h-8 px-3 rounded-lg btn-gradient text-[11.5px] font-bold inline-flex items-center gap-1"
              data-testid="property-directions-btn"
            >
              <Navigation size={12} /> Directions
            </a>
          )}
          {!isApproximate && gmapsUrl && (
            <a
              href={gmapsUrl}
              target="_blank"
              rel="noreferrer"
              className="h-8 px-3 rounded-lg border border-zinc-200 text-[11.5px] font-bold text-zinc-700 hover:border-[#0055FF] hover:text-[#0055FF] inline-flex items-center gap-1"
              data-testid="property-gmaps-btn"
            >
              <ExternalLink size={11} /> Google Maps
            </a>
          )}
          <button
            type="button"
            onClick={() => setFs(!fs)}
            className="h-8 w-8 rounded-lg border border-zinc-200 text-zinc-700 hover:border-[#0055FF] hover:text-[#0055FF] inline-flex items-center justify-center"
            aria-label={fs ? "Exit fullscreen" : "Open fullscreen map"}
            data-testid="property-mini-map-fs"
          >
            {fs ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>

      {/* Map */}
      <div className={`rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-100 ${fs ? "mx-5 mb-5 h-[calc(100vh-120px)]" : "aspect-[16/10] sm:aspect-video"}`}>
        <MapContainer center={[lat, lng]} zoom={isApproximate ? 13 : 15} scrollWheelZoom={false} className="w-full h-full" style={{ zIndex: 0 }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          {isApproximate ? (
            // v2.85 Phase A — Privacy-preserving render: ~300m radius
            // translucent circle, NO precise marker. Indicates the
            // general locality without exposing the exact street pin.
            // Radius defaults to 300m to match the original UX, but
            // honours `approx_precision_m` if the backend sends a
            // different value.
            <Circle
              center={[lat, lng]}
              radius={approxRadius}
              pathOptions={{ color: "#0055FF", fillColor: "#0055FF", fillOpacity: 0.18, weight: 2 }}
              data-testid="property-mini-map-approx-circle"
            />
          ) : (
            <Marker position={[lat, lng]} icon={propertyIcon}>
              <Popup>
                <div className="font-bold text-zinc-900 text-[12px]">{property.title || "Property"}</div>
                {property.area && <div className="text-[11px] text-zinc-600">{property.area}</div>}
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* v2.85 Phase A — Privacy note for approximate mode */}
      {isApproximate && (
        <div className={`mt-2 text-[11px] text-zinc-500 ${fs ? "px-5" : ""}`} data-testid="property-approx-note">
          {property?.map_locked
            ? "Showing approximate area (within ~300m). Upgrade your plan or contact the lister to see the exact pin and directions."
            : "Showing approximate area. Sign in or contact the lister to get the exact pin and directions."}
        </div>
      )}

      {/* Nearby strip (hint for now — pulls metro distance if present) */}
      <div className={`mt-3 ${fs ? "px-5 pb-5" : ""}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">Nearby</div>
        <div className="flex flex-wrap gap-2">
          {NEARBY_DEFAULTS.map(({ Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-zinc-200 text-[11px] font-bold text-zinc-700">
              <Icon size={11} className="text-[#0055FF]" /> {label}
            </span>
          ))}
          {property.distance_metro_m != null && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold text-emerald-800">
              <Train size={11} /> {Math.round(property.distance_metro_m)}m to metro
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
